// Global packages and components
import { clsx } from 'clsx'
import React from 'react'

// Embla
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from '~/components/partials/embla/prev-next'
import { DotButton, useDotButton } from '~/components/partials/embla/dots'
import { ThumbButton, useThumbs } from '~/components/partials/embla/thumbs'

// Types
import type { EmblaCarouselType } from 'embla-carousel'

export type DotColors = 'white' | 'grey' | 'gold' | 'teal'

type ArrowsPropType = {
  emblaApi: EmblaCarouselType | undefined
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
  classNames: {
    prev?: string
    next?: string
  }
}

type DotsPropType = {
  emblaApi: EmblaCarouselType | undefined
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
  classNames: {
    container?: string
    dot?: string
    dotColor?: DotColors
    dotActiveColor?: DotColors
  }
}

type ThumbsPropType = {
  emblaMainApi: EmblaCarouselType | undefined
  emblaThumbsApi: EmblaCarouselType | undefined
  thumbs: Array<{ image: { src: string; alt: string }; title?: string }>
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
}

// Exports
export const EmblaArrows: React.FC<ArrowsPropType> = props => {
  const { emblaApi, onButtonClick, classNames } = props

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onButtonClick)

  return (
    <>
      <PrevButton
        className={clsx(
          `embla__arrow embla__arrow--prev transition-opacity ${
            classNames?.prev ?? 'text-dark-grey absolute left-0 top-2/4 p-2'
          }`,
          {
            'opacity-20': prevBtnDisabled,
          }
        )}
        onClick={onPrevButtonClick}
        disabled={prevBtnDisabled}
      />
      <NextButton
        className={clsx(
          `embla__arrow embla__arrow--next transition-opacity ${
            classNames?.next ?? 'text-dark-grey absolute right-0 top-2/4 p-2'
          }`,
          {
            'opacity-20': nextBtnDisabled,
          }
        )}
        onClick={onNextButtonClick}
        disabled={nextBtnDisabled}
      />
    </>
  )
}

export const EmblaDots: React.FC<DotsPropType> = props => {
  const { emblaApi, onButtonClick, classNames } = props

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onButtonClick
  )

  return (
    <div className={clsx(`embla__dots flex ${classNames?.container ?? ''}`)}>
      {scrollSnaps.map((_, index) => (
        <DotButton
          key={index}
          onClick={() => onDotButtonClick(index)}
          className={clsx(
            `embla__dot transition-opacity ${
              classNames?.dot ?? 'm-1 h-3 w-3 rounded-full'
            }`,
            {
              'bg-white':
                (index == selectedIndex &&
                  classNames?.dotActiveColor == 'white') ||
                (index !== selectedIndex && classNames?.dotColor == 'white'),
              'bg-gold-500':
                (index == selectedIndex &&
                  classNames?.dotActiveColor == 'gold') ||
                (index !== selectedIndex && classNames?.dotColor == 'gold'),
              'bg-dark-grey':
                (index == selectedIndex &&
                  classNames?.dotActiveColor == 'grey') ||
                (index !== selectedIndex && classNames?.dotColor == 'grey'),
              'bg-teal-500':
                (index == selectedIndex &&
                  classNames?.dotActiveColor == 'teal') ||
                (index !== selectedIndex && classNames?.dotColor == 'teal'),
              'embla__dot--active': selectedIndex,
            }
          )}
        />
      ))}
    </div>
  )
}

export const EmblaThumbs: React.FC<ThumbsPropType> = props => {
  const { emblaMainApi, emblaThumbsApi, thumbs, onButtonClick } = props

  const { selectedIndex, onThumbClick } = useThumbs(
    emblaMainApi,
    emblaThumbsApi,
    onButtonClick
  )

  return thumbs.map((thumb, i) => (
    <ThumbButton
      onClick={() => onThumbClick(i)}
      selected={i === selectedIndex}
      index={i}
      key={i}
      title={thumb.title}
      img={thumb.image}
    />
  ))
}
